import React from "react";
import { Helmet } from "react-helmet";
import {
  absoluteUrl,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_ALT,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  formatPageTitle,
  FB_APP_ID,
  SITE_NAME,
  truncateDescription,
} from "helpers/seo/siteConfig";
import { buildJsonLdGraph } from "helpers/seo/schema";

const PageSeo = ({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  ogImageWidth = DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight = DEFAULT_OG_IMAGE_HEIGHT,
  ogType = "website",
  noindex = false,
  jsonLd,
}) => {
  const pageTitle = formatPageTitle(title);
  const pageDescription = truncateDescription(description);
  const canonicalUrl = absoluteUrl(path);
  const imageUrl = absoluteUrl(ogImage);
  const isExternalImage =
    typeof ogImage === "string" &&
    (/^https?:\/\//i.test(ogImage) || ogImage.startsWith("//"));
  const imageType =
    !isExternalImage && ogImage.endsWith(".png") ? "image/png" : "image/jpeg";

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={imageUrl} />
      {!isExternalImage ? (
        <meta property="og:image:secure_url" content={imageUrl} />
      ) : null}
      {!isExternalImage ? (
        <meta property="og:image:width" content={String(ogImageWidth)} />
      ) : null}
      {!isExternalImage ? (
        <meta property="og:image:height" content={String(ogImageHeight)} />
      ) : null}
      {!isExternalImage ? (
        <meta property="og:image:type" content={imageType} />
      ) : null}
      {isExternalImage ? (
        <meta property="og:image:secure_url" content={imageUrl} />
      ) : null}
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="en_US" />
      {FB_APP_ID ? <meta property="fb:app_id" content={FB_APP_ID} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      <link rel="image_src" href={imageUrl} />

      {jsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(buildJsonLdGraph(Array.isArray(jsonLd) ? jsonLd : [jsonLd]))}
        </script>
      ) : null}
    </Helmet>
  );
};

export default PageSeo;
